import React, { useEffect, useRef, useState } from "react";
import Navbar from "../../Company/About/Navbar";
import Footer from "../../Company/About/Footer";
import { Container } from '@/components/ui/layout';
import { Card, CardContent } from '@/components/ui/card';
import { Heading, Text } from '@/components/ui/typography';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, AlertCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function QuestionnairePage() {
  const [loading, setLoading] = useState(true);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState({});
  const [saveErrors, setSaveErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const saveTimeoutsRef = useRef(new Map());

  useEffect(() => {
    // Get questionnaire ID from URL
    const params = new URLSearchParams(window.location.search);
    const questionnaireId = params.get("id");

    if (!questionnaireId) {
      setError("Invalid questionnaire link");
      setLoading(false);
      return;
    }

    fetchQuestionnaire(questionnaireId);
  }, []);

  useEffect(() => {
    return () => {
      saveTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
      saveTimeoutsRef.current.clear();
    };
  }, []);

  const fetchQuestionnaire = async (id) => {
    try {
      const response = await fetch(`/api/questionnaire/${id}`);
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Failed to load questionnaire");
        setLoading(false);
        return;
      }

      setQuestionnaire(data.questionnaire);
      setQuestions(data.questions);

      // Pre-fill existing answers
      const existingAnswers = {};
      data.questions.forEach((q) => {
        if (q.answer_text !== null && q.answer_text !== undefined) {
          existingAnswers[q.question_id] = q.answer_text;
        }
      });
      setAnswers(existingAnswers);

      setLoading(false);
    } catch (err) {
      console.error("Error fetching questionnaire:", err);
      setError("Failed to load questionnaire. Please try again later.");
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    // Update local state
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
    setSaveErrors((prev) => {
      if (!prev[questionId]) return prev;
      const next = { ...prev };
      delete next[questionId];
      return next;
    });

    // Debounce independently per question so editing one response never
    // cancels another question's pending draft save.
    const existingTimeout = saveTimeoutsRef.current.get(questionId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    const timeoutId = setTimeout(() => {
      saveTimeoutsRef.current.delete(questionId);
      saveAnswer(questionId, value).catch((err) => {
        console.error("Error saving answer:", err);
        setSaveErrors((prev) => ({ ...prev, [questionId]: err.message }));
      });
    }, 1000);
    saveTimeoutsRef.current.set(questionId, timeoutId);
  };

  const saveAnswer = async (questionId, value) => {
    setSaving((prev) => ({ ...prev, [questionId]: true }));

    try {
      const response = await fetch(
        `/api/questionnaire/${questionnaire.questionnaire_id}/answer`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            question_id: questionId,
            answer_text: value,
          }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to save answer");
      }
      return data;
    } finally {
      setSaving((prev) => ({ ...prev, [questionId]: false }));
    }
  };

  const validateAnswers = () => {
    const errors = [];
    questions.forEach((q) => {
      if (q.is_required) {
        const answer = answers[q.question_id];
        if (typeof answer !== "string" || !answer.trim()) {
          errors.push({
            questionId: q.question_id,
            text: q.question_text,
          });
        }
      }
    });
    return errors;
  };

  const handleSubmit = async () => {
    // Validate all required questions
    const errors = validateAnswers();
    if (errors.length > 0) {
      setValidationErrors(errors);
      // Scroll to first error
      const firstErrorElement = document.getElementById(
        `question-${errors[0].questionId}`
      );
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setValidationErrors([]);
    setSubmitting(true);

    // Submit is authoritative. Cancel pending draft saves because the current
    // React state is included in this request and saved atomically by the API.
    saveTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    saveTimeoutsRef.current.clear();

    const submittedAnswers = questions.map((question) => {
      const answer = answers[question.question_id];
      return {
        question_id: question.question_id,
        answer_text:
          typeof answer === "string"
            ? answer
            : answer === null || answer === undefined
              ? ""
              : String(answer),
      };
    });

    try {
      const response = await fetch(
        `/api/questionnaire/${questionnaire.questionnaire_id}/submit`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ answers: submittedAnswers }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (Array.isArray(data.unanswered)) {
          setValidationErrors(
            data.unanswered.map((question) => ({
              questionId: question.question_id,
              text: question.question_text,
            }))
          );
          setSubmitting(false);
          return;
        }
        setError(data.error || "Failed to submit questionnaire");
        setSubmitting(false);
        return;
      }

      setSubmitted(true);
    } catch (err) {
      console.error("Error submitting questionnaire:", err);
      setError("Failed to submit questionnaire. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className={cn("min-h-screen flex items-center justify-center")}>
        <Card className={cn("p-8 text-center")}>
          <CardContent className={cn("p-0")}>
            <Loader2 className={cn("w-12 h-12 animate-spin mx-auto mb-4 text-primary")} />
            <Text>Loading questionnaire...</Text>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className={cn("min-h-screen flex items-center justify-center")}>
        <Card className={cn("p-8 text-center")}>
          <CardContent className={cn("p-0")}>
            <AlertCircle className={cn("w-12 h-12 mx-auto mb-4 text-destructive")} />
            <Heading level="h2" className={cn("text-2xl font-bold mb-2")}>Oops! Something went wrong</Heading>
            <Text className={cn("mb-4")}>{error}</Text>
            <Button onClick={() => window.location.reload()}>Reload Page</Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (submitted) {
    return (
      <Container className={cn("min-h-screen flex items-center justify-center")}>
        <Card className={cn("p-8 text-center")}>
          <CardContent className={cn("p-0")}>
            <CheckCircle2 className={cn("w-12 h-12 mx-auto mb-4 text-green-500")} />
            <Heading level="h2" className={cn("text-2xl font-bold mb-2")}>Thank You!</Heading>
            <Text className={cn("mb-2")}>Your responses have been submitted successfully.</Text>
            <Text className={cn("text-muted-foreground mb-4")}>
              We appreciate you taking the time to complete this questionnaire.
              We'll be in touch soon!
            </Text>
            <Button onClick={() => window.location.href = "/user-home"}>Go to Home</Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  const requiredCount = questions.filter((q) => q.is_required).length;
  const answeredRequired = questions.filter(
    (q) => q.is_required && answers[q.question_id]?.trim()
  ).length;
  const progress = requiredCount > 0 ? (answeredRequired / requiredCount) * 100 : 0;

  return (
    <>
      <Navbar />
      <Container className={cn("min-h-screen py-8")}>
        <Card className={cn("max-w-4xl mx-auto")}>
          <CardContent className={cn("p-6 md:p-8")}>
            <header className={cn("mb-8")}>
              <Heading level="h1" className={cn("text-3xl font-bold mb-2")}>{questionnaire.job_title}</Heading>
              <Text className={cn("text-lg text-muted-foreground mb-1")}>{questionnaire.company_name}</Text>
              <Text className={cn("text-base mb-4")}>Hello {questionnaire.candidate_name},</Text>
              <Text className={cn("text-sm text-muted-foreground")}>
                Please answer the following questions. Fields marked with{" "}
                <span className={cn("text-destructive")}>*</span> are mandatory.
              </Text>
            </header>

            {/* Progress Bar */}
            <div className={cn("mb-6")}>
              <div className={cn("flex justify-between items-center mb-2")}>
                <Text className={cn("text-sm font-medium")}>
                  Progress: {answeredRequired} / {requiredCount} required questions
                </Text>
                <Text className={cn("text-sm font-semibold")}>{Math.round(progress)}%</Text>
              </div>
              <Progress value={progress} className={cn("h-2")} />
            </div>

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <Alert variant="destructive" className={cn("mb-6")}>
                <AlertTriangle className={cn("h-4 w-4")} />
                <AlertDescription>
                  <strong>Please answer all mandatory questions:</strong>
                  <ul className={cn("list-disc list-inside mt-2 space-y-1")}>
                    {validationErrors.map((err) => (
                      <li key={err.questionId}>
                        <button
                          className={cn("underline hover:no-underline")}
                          onClick={(e) => {
                            e.preventDefault();
                            document
                              .getElementById(`question-${err.questionId}`)
                              ?.scrollIntoView({ behavior: "smooth", block: "center" });
                          }}
                        >
                          {err.text}
                        </button>
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Questions */}
            <div className={cn("space-y-6 mb-8")}>
              {questions.map((question, index) => {
                const hasError = validationErrors.some(
                  (err) => err.questionId === question.question_id
                );
                const isSaving = saving[question.question_id];

                return (
                  <Card
                    key={question.question_id}
                    id={`question-${question.question_id}`}
                    className={cn("p-6", hasError && "border-destructive")}
                  >
                    <CardContent className={cn("p-0")}>
                      <div className={cn("flex items-center justify-between mb-4")}>
                        <Text className={cn("text-sm font-medium text-muted-foreground")}>
                          Question {index + 1}
                        </Text>
                        <div className={cn("flex items-center gap-2")}>
                          {question.is_required > 0 && (
                            <span className={cn("px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded")}>
                              Required
                            </span>
                          )}
                          {isSaving && (
                            <Text className={cn("text-xs text-muted-foreground flex items-center gap-1")}>
                              <Loader2 className={cn("w-3 h-3 animate-spin")} />
                              Saving...
                            </Text>
                          )}
                        </div>
                      </div>

                      <Label htmlFor={`answer-${question.question_id}`} className={cn("text-base font-medium mb-2 block")}>
                        {question.question_text}
                        {question.is_required && (
                          <span className={cn("text-destructive ml-1")}>*</span>
                        )}
                      </Label>

                      <Textarea
                        id={`answer-${question.question_id}`}
                        placeholder="Type your answer here..."
                        value={answers[question.question_id] || ""}
                        onChange={(e) =>
                          handleAnswerChange(question.question_id, e.target.value)
                        }
                        rows={5}
                        className={cn("mt-2", hasError && "border-destructive")}
                      />

                      {hasError && (
                        <Text className={cn("text-sm text-destructive mt-2")}>This question is required</Text>
                      )}
                      {saveErrors[question.question_id] && (
                        <Text className={cn("text-sm text-destructive mt-2")}>
                          Draft save failed. This answer will still be included when you submit.
                        </Text>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Submit Button */}
            <div className={cn("flex flex-col items-center gap-4")}>
              <Text className={cn("text-sm text-muted-foreground text-center")}>
                Make sure to review your answers before submitting.
              </Text>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className={cn("w-full sm:w-auto min-w-[200px]")}
                size="lg"
              >
                {submitting ? (
                  <>
                    <Loader2 className={cn("w-4 h-4 mr-2 animate-spin")} />
                    Submitting...
                  </>
                ) : (
                  "Submit Questionnaire"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </Container>
      <Footer />
    </>
  );
}
